/**
 * The hand-off from a store's own pages.
 *
 * A storefront link can send the shopper here as `/<slug>/returns#order=<ref>&phone=<mobile>`.
 * A URL fragment is never sent to any server, so the shopper's mobile reaches only this page,
 * and only as a pre-fill: the lookup that follows is the ordinary one, verified server-side
 * against the Salla order exactly as if she had typed both fields. The fragment is cleared as
 * soon as it is read, so a reload does not repeat the lookup.
 */
export interface Prefill {
  orderRef: string | null;
  phone: string | null;
}

export function parsePrefill(hash: string): Prefill {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (raw === "") return { orderRef: null, phone: null };
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return { orderRef: null, phone: null };
  }
  const clean = (value: string | null): string | null => {
    const trimmed = value?.trim() ?? "";
    return trimmed === "" || trimmed.length > 64 ? null : trimmed;
  };
  return {
    orderRef: clean(params.get("order")),
    phone: clean(params.get("phone")),
  };
}

/** Read the hand-off without consuming it, to decide which form to show first. */
export function peekPrefill(): Prefill {
  if (typeof window === "undefined") return { orderRef: null, phone: null };
  return parsePrefill(window.location.hash);
}

/** Reads and clears the fragment in one move. Safe outside a browser. */
export function takePrefill(): Prefill {
  if (typeof window === "undefined") return { orderRef: null, phone: null };
  const prefill = parsePrefill(window.location.hash);
  if (window.location.hash !== "") {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }
  return prefill;
}
